import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/client";

import { GET_SUBCATEGORY_MEASUREMENT_ITEMS } from "../../query/measurementQueries";
import ProductMeasurementList from "./ProductMeasurementList";

const MEASUREMENT_DISCLAIMER = "측정 방법에 따라 ±1~3cm 오차가 있을 수 있어요.";

/**
 * 가이드 이미지
 */
function GuideImage({ src, alt }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <img
      className="ProductMeasurementModal-guide-image"
      data-loaded={loaded}
      src={src}
      alt={alt}
      decoding="async"
      onLoad={() => setLoaded(true)}
      onError={() => setLoaded(true)}
    />
  );
}

GuideImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
};

/**
 * 실측 안내 모달.
 * - PC: 화면 중앙 오버레이 모달
 * - 모바일: 화면 하단에서 올라오는 바텀시트 (반응형 CSS로 분기)
 * @param {Array} measurements - 상품에 입력된 실측값 [{label, key, value}]
 * @param {string} subcategoryName - 서브카테고리 이름 (가이드/전체 항목 조회 키)
 * @param {() => void} onClose - 모달 닫기 콜백
 */
export default function ProductMeasurementModal({
  measurements,
  subcategoryName,
  onClose,
}) {
  const { data, loading } = useQuery(GET_SUBCATEGORY_MEASUREMENT_ITEMS, {
    variables: { subcategoryName },
    skip: !subcategoryName,
    fetchPolicy: "cache-first",
  });

  const baseItems = data?.getSubcategoryMeasurementItems?.measurement_item;
  const rawGuideImages =
    data?.getSubcategoryMeasurementItems?.subcategory?.guide_image;
  const guideImages = useMemo(() => {
    if (Array.isArray(rawGuideImages)) return rawGuideImages.filter(Boolean);
    return rawGuideImages ? [rawGuideImages] : [];
  }, [rawGuideImages]);

  // 전체 측정 항목 기준으로 정렬하고, 미입력 항목은 '–' 로 채워 병합
  const mergedMeasurements = useMemo(() => {
    if (!Array.isArray(baseItems) || baseItems.length === 0) {
      return measurements ?? null;
    }
    const valueMap = new Map(
      (measurements ?? []).map((m) => [m.label, m.value]),
    );
    return baseItems.map((base) => ({
      label: base.label,
      key: base.key,
      value: valueMap.get(base.label) ?? null,
    }));
  }, [baseItems, measurements]);

  // 배경 스크롤 잠금 + ESC 로 닫기
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="ProductMeasurementModal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="ProductMeasurementModal"
        role="dialog"
        aria-modal="true"
        aria-label="실측 안내"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="ProductMeasurementModal-header">
          <h2 className="ProductMeasurementModal-title">실측</h2>
          <button
            type="button"
            className="ProductMeasurementModal-close"
            aria-label="닫기"
            onClick={onClose}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M6 6L18 18M18 6L6 18"
                stroke="#1F1F1F"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="ProductMeasurementModal-body">
          {guideImages.length > 0 && (
            <div className="ProductMeasurementModal-guide">
              {guideImages.map((image, index) => (
                <GuideImage
                  key={image ?? index}
                  src={image}
                  alt={`${subcategoryName ?? ""} 실측 측정 방법 안내 이미지${index + 1}`}
                />
              ))}
            </div>
          )}

          <p className="ProductMeasurementModal-disclaimer">
            {MEASUREMENT_DISCLAIMER}
          </p>

          <ProductMeasurementList
            measurements={mergedMeasurements}
            className="ProductMeasurementModal-list"
          />

          {loading && !mergedMeasurements && (
            <p className="ProductMeasurementModal-loading">불러오는 중…</p>
          )}
        </div>
      </div>
    </div>
  );
}

ProductMeasurementModal.propTypes = {
  measurements: PropTypes.array,
  subcategoryName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};
